// @flow

// ----------
// ⌒°(❛ᴗ❛)°⌒
// ----------
import "./polyfills"

import * as React from "react"
import ReactDom from "react-dom"
import {Provider} from "react-redux"
import {createBrowserHistory} from "history"
import Sortable from "sortablejs"
import flatpickr from "flatpickr"
import {connect} from "react-redux"

import Loadable from "./loadable"
import ErrorHOC from "./services/sentry"
import configureStore from "./store/configureStore"
import App from "./app"
import MobileNav from "./components/mobile-nav"
import BabyCard from "./components/baby-card"
import type {State} from "./types"
import Pickr from "./pickr"

window.menageHistory = createBrowserHistory()

// empty import, kick off for i18next
import i18n from "./i18n" // eslint-disable-line no-unused-vars
import {I18nextProvider} from "react-i18next"

/********************************************************/

// sets Dinero defaults, keep import!
import Dinero from "dinero.js"

Dinero.defaultCurrency = "EUR"
Dinero.globalLocale = "nl-NL"

/********************************************************/

import {Socket} from "phoenix"
import {LiveSocket} from "phoenix_live_view"

const hooks = {
  Pickr,
}

let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content")
let liveSocket = new LiveSocket("/live", Socket, {
  hooks,
  params: {_csrf_token: csrfToken},
})

// Connect if there are any LiveViews on the page
liveSocket.connect()

// Expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)
// The latency simulator is enabled for the duration of the browser session.
// Call disableLatencySim() to disable:
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

/********************************************************/

const store = configureStore()
const appEl = document.getElementById("app")

function bootApp() {
  if (appEl) {
    ReactDom.render(
      <Provider store={store}>
        <ErrorHOC>
          <App />
        </ErrorHOC>
      </Provider>,
      appEl
    )
  }
}

function bootExtraData() {}

function bootCore(): void {
  bootApp()
  bootExtraData()
}

function bootComponents() {
  bootUploaders()
  new MobileNav()

  const comp = document.querySelectorAll(".c-baby-card-actions")
  Array.from(comp).forEach((comp) => {
    const mapStateToProps = (state: State) => ({
      user: state.user.entity,
      baby: JSON.parse(comp.getAttribute("data-baby")),
      latestOfferDeposit: comp.getAttribute("data-latest-offer-deposit"),
      currentDeposit: comp.getAttribute("data-current-deposit"),
      isAdmin: comp.getAttribute("data-is-admin") == "true",
      onlyActions: true,
    })
    const CompHOC = connect(mapStateToProps)(BabyCard)
    ReactDom.render(
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ErrorHOC>
            <CompHOC />
          </ErrorHOC>
        </I18nextProvider>
      </Provider>,
      comp
    )
  })
}

function bootUploaders() {
  // regular uploaders
  const AsyncUploader = Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "components-uploader" */ "./components/uploader"
      ),
  })
  const comp = document.querySelectorAll(".c-uploader")
  Array.from(comp).forEach((comp) => {
    const mapStateToProps = (state: State) => ({
      user: state.user.entity,
      shopProductId: comp.getAttribute("data-shop-product-id"),
      productId: comp.getAttribute("data-product-id"),
      productSetId: comp.getAttribute("data-product-set-id"),
      mediaId: comp.getAttribute("data-media-id"),
      isStandalone: comp.getAttribute("data-is-standalone") == "true",
    })
    const CompHOC = connect(mapStateToProps)(AsyncUploader)
    ReactDom.render(
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ErrorHOC>
            <CompHOC />
          </ErrorHOC>
        </I18nextProvider>
      </Provider>,
      comp
    )
  })
  // shop shop image uploader
  const AsyncShopShopImageUploader = Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "components-admin-shop-image-uploader" */ "./components/shop/admin/shop-shop-image-uploader.js"
      ),
  })
  const compShopShopImage = document.querySelectorAll(
    ".c-shop-shop-image-uploader"
  )
  Array.from(compShopShopImage).forEach((comp) => {
    const mapStateToProps = () => ({
      shopName: comp.getAttribute("data-shop-name"),
      shopToken: comp.getAttribute("data-shop-token"),
      shopId: comp.getAttribute("data-shop-id"),
      shopImage: comp.getAttribute("data-shop-image"),
    })
    const CompShopShopImageHOC = connect(mapStateToProps)(
      AsyncShopShopImageUploader
    )
    ReactDom.render(
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ErrorHOC>
            <CompShopShopImageHOC />
          </ErrorHOC>
        </I18nextProvider>
      </Provider>,
      comp
    )
  })
  // shop baby image uploader
  const AsyncShopBabyImageUploader = Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "components-admin-shop-image-uploader" */ "./components/shop/admin/shop-baby-image-uploader.js"
      ),
  })
  const compShopBabyImage = document.querySelectorAll(
    ".c-shop-baby-image-uploader"
  )
  Array.from(compShopBabyImage).forEach((comp) => {
    const mapStateToProps = () => ({
      shopName: comp.getAttribute("data-shop-name"),
      shopToken: comp.getAttribute("data-shop-token"),
      shopId: comp.getAttribute("data-shop-id"),
      babyImage: comp.getAttribute("data-baby-image"),
    })
    const CompShopBabyImageHOC = connect(mapStateToProps)(
      AsyncShopBabyImageUploader
    )
    ReactDom.render(
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ErrorHOC>
            <CompShopBabyImageHOC />
          </ErrorHOC>
        </I18nextProvider>
      </Provider>,
      comp
    )
  })
  // baby media uploader
  const AsyncBabyMediaUploader = Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "components-baby-media-uploader" */ "./components/baby-media-uploader.js"
      ),
  })
  const compBabyMedia = document.querySelectorAll(".c-baby-media-uploader")
  Array.from(compBabyMedia).forEach((comp) => {
    const mapStateToProps = () => ({
      babyId: comp.getAttribute("data-baby-id"),
    })
    const CompBabyMediaHOC = connect(mapStateToProps)(AsyncBabyMediaUploader)
    renderUploaderComp(<CompBabyMediaHOC />, comp)
  })
  // avatar uploader
  const AsyncAvatarUploader = Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "components-avatar-uploader" */ "./components/avatar-uploader.js"
      ),
  })
  const compAvatar = document.querySelectorAll(".c-avatar-uploader")
  Array.from(compAvatar).forEach((comp) => {
    const mapStateToProps = () => ({
      userId: comp.getAttribute("data-user-id"),
      avatar: comp.getAttribute("data-avatar"),
    })
    const CompAvatarHOC = connect(mapStateToProps)(AsyncAvatarUploader)
    ReactDom.render(
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ErrorHOC>
            <CompAvatarHOC />
          </ErrorHOC>
        </I18nextProvider>
      </Provider>,
      comp
    )
  })
}

function renderUploaderComp(comp, el) {
  ReactDom.render(
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <ErrorHOC>{comp}</ErrorHOC>
      </I18nextProvider>
    </Provider>,
    el
  )
}

/**
 *
 */
function bootLibraries() {
  // dateFormat: date formatting for server
  // altFormat: date formatting for user
  // altInput: use altFormat for user and dateFormat for server
  flatpickr(".flatpickr", {
    dateFormat: "Y-m-d",
    altFormat: "d/m/Y",
    altInput: true,
  })
  const timeProps = {
    enableTime: true,
    time_24hr: true,
    dateFormat: "Z",
    altFormat: "d/m/Y H:i",
    altInput: true,
  }
  flatpickr(".flatpickr-with-time", timeProps)
  flatpickr(".flatpickr-with-time-static", {...timeProps, static: true})
}

/**
 *
 */
function initSortableTables() {
  const containers = document.querySelectorAll(".sortable-table")
  Array.from(containers).forEach((container) => initSortableTable(container))
}

function initSortableTable(container: HTMLElement) {
  Sortable.create(container, {
    animation: 100,
    handle: ".sortable-table__handle",
    draggable: "tr",
    onUpdate: () => {
      updateAllOrders()
      saveAllOrders()
    },
  })

  function getItems(): HTMLInputElement[] {
    return Array.from(container.querySelectorAll(".sortable-table__meta input"))
  }

  function getItemsByType(type): HTMLInputElement[] {
    return Array.from(
      container.querySelectorAll(
        `.sortable-table__meta input[name$='${type}]']`
      )
    )
  }

  function updateAllOrders() {
    const items = getItemsByType("order")
    const itemOrders = items.map((item) => parseInt(item.value))
    const lowestOrder = Math.min(...itemOrders)
    items.forEach((item, index) => {
      item.value = (lowestOrder + index).toString()
    })
  }

  function saveAllOrders() {
    const items = getItems()
    const formData = new FormData()

    items.forEach((item) => {
      const name = item.getAttribute("name") || ""
      formData.append(name, item.value)
    })

    const request = new XMLHttpRequest()
    request.open("POST", window.menageConfig.apiUrl + "/admin/sorting")
    request.setRequestHeader("X-CSRF-Token", window.menageConfig._csrf_token)
    request.send(formData)
  }
}

/**
 * Boot start
 */
if (appEl) {
  bootCore()
}

bootLibraries()
bootComponents()
initSortableTables()

export default {}
